import React, { useRef, useState } from 'react';
import { Modal, Chip } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import OndemandVideoOutlinedIcon from '@material-ui/icons/OndemandVideoOutlined';
import MenuBookOutlinedIcon from '@material-ui/icons/MenuBookOutlined';
import Button from '../../../components/common/Button';
import CoursesAPI from '../../CoursesContainer/course-api';
import workshopApi from '../../WorkShopContainer/workshop-api';
import Alert from '../../../components/common/Alert';

const normalizeEmail = email => email.trim().toLowerCase();
const isValidEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const uniqueEmails = emails => [...new Set(emails.map(normalizeEmail))];

const isValidDate = value => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return year >= 1 && month >= 1 && month <= 12 && day >= 1 && day <= days[month - 1];
};

export const validateWorkshopReportDates = (startDate, endDate) => {
  if (startDate && !isValidDate(startDate)) return 'Ingresa una fecha desde válida.';
  if (endDate && !isValidDate(endDate)) return 'Ingresa una fecha hasta válida.';
  if (startDate && endDate && startDate > endDate) {
    return 'La fecha desde no puede ser posterior a la fecha hasta.';
  }
  return '';
};

const EmailInput = ({ emails, setEmails, value, onChange, error, onAdd, disabled }) => (
  <div className="report-modal__email">
    <label className="form-wrapper form-wrapper--full" htmlFor="report-email">
      <span className="form-wrapper__label">Correo electrónico</span>
      <div className="text-input">
        <input
          id="report-email"
          type="email"
          value={value}
          placeholder="nombre@ejemplo.cl"
          onChange={event => onChange(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onAdd();
            }
          }}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? 'report-email-error' : 'report-email-help'}
          disabled={disabled}
        />
      </div>
    </label>
    {error ? (
      <span id="report-email-error" className="report-modal__field-error" role="alert">{error}</span>
    ) : (
      <span id="report-email-help" className="form-wrapper__label--description">Escribe un correo y presiona Enter para agregarlo.</span>
    )}
    <div className="report-modal__chips">
      {emails.map(email => (
        <Chip
          key={email}
          label={email}
          disabled={disabled}
          onDelete={() => setEmails(current => current.filter(item => item !== email))}
        />
      ))}
    </div>
  </div>
);

const ReportModal = ({ open, announcementId, onClose, title, mainText, action }) => {
  const [emails, setEmails] = useState([]);
  const [pendingEmail, setPendingEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState({ type: '', message: '' });
  const requestInProgress = useRef(false);
  const generation = useRef(0);
  const startDateInput = useRef(null);
  const endDateInput = useRef(null);
  const isWorkshopReport = action === 'report-workshops';

  const handleClose = () => {
    generation.current += 1;
    setAlertMessage({ type: '', message: '' });
    setEmails([]);
    setPendingEmail('');
    setEmailError('');
    setStartDate('');
    setEndDate('');
    onClose();
  };

  const addEmail = () => {
    if (requestInProgress.current) return;
    const email = normalizeEmail(pendingEmail);
    if (!isValidEmail(email)) {
      setEmailError('Por favor ingresa una dirección de correo válida.');
      return;
    }
    setEmails(current => uniqueEmails([...current, email]));
    setPendingEmail('');
    setEmailError('');
  };

  const requestReport = async () => {
    if (requestInProgress.current) return;
    const pending = normalizeEmail(pendingEmail);
    const recipients = uniqueEmails([...emails, ...(pending ? [pending] : [])]);
    if (recipients.some(email => !isValidEmail(email))) {
      setEmailError('Por favor ingresa una dirección de correo válida.');
      setAlertMessage({ type: 'error', message: 'Revisa el correo pendiente antes de solicitar el reporte.' });
      return;
    }
    if (recipients.length === 0) {
      setAlertMessage({ type: 'error', message: 'Por favor agrega al menos un correo.' });
      return;
    }
    if (isWorkshopReport) {
      if (recipients.length > 20) {
        setAlertMessage({ type: 'error', message: 'Puedes agregar hasta 20 correos únicos para el reporte de talleres.' });
        return;
      }
      const dateError = startDateInput.current?.validity.badInput
        ? 'Ingresa una fecha desde válida.'
        : endDateInput.current?.validity.badInput
          ? 'Ingresa una fecha hasta válida.'
          : validateWorkshopReportDates(startDate, endDate);
      if (dateError) {
        setAlertMessage({ type: 'error', message: dateError });
        return;
      }
    }
    if (!['report-courses', 'report-workshops'].includes(action)) {
      setAlertMessage({ type: 'error', message: 'No se pudo identificar el reporte solicitado.' });
      return;
    }

    requestInProgress.current = true;
    const currentGeneration = generation.current;
    setLoading(true);
    setAlertMessage({ type: '', message: '' });
    setEmails(recipients);
    setPendingEmail('');
    setEmailError('');
    try {
      if (isWorkshopReport) {
        await workshopApi.requestAnnouncementReport(announcementId, {
          emails: recipients,
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
        });
      } else {
        await CoursesAPI.requestAnnouncementReport(announcementId, { emails: recipients });
      }
      if (currentGeneration === generation.current) {
        setAlertMessage({ type: 'success', message: 'Se ha generado tu solicitud de reporte exitosamente. Revisa tu correo en unos minutos.' });
      }
    } catch (error) {
      if (currentGeneration === generation.current) {
        const apiMessage = error?.data?.message;
        setAlertMessage({
          type: 'error',
          message: typeof apiMessage === 'string' && apiMessage.trim()
            ? apiMessage
            : 'Ocurrió un error al solicitar el informe. Por favor, inténtalo de nuevo.',
        });
      }
    } finally {
      requestInProgress.current = false;
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} className="modal report-modal">
      <section
        className="announcement-form announcement-form__card report-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-modal-title"
        aria-describedby="report-modal-description"
        tabIndex={-1}
      >
        <header className="announcement-form__section-header report-modal__header">
          <span className="announcement-form__section-icon" aria-hidden="true">
            {isWorkshopReport ? <OndemandVideoOutlinedIcon /> : <MenuBookOutlinedIcon />}
          </span>
          <div className="report-modal__heading">
            <h3 id="report-modal-title">{title || 'Generar reporte'}</h3>
            <p id="report-modal-description">
              {isWorkshopReport
                ? 'Recibe el detalle de talleres por persona, fecha y semana.'
                : mainText || 'Recibe el detalle de cursos de la convocatoria.'}
            </p>
          </div>
          <button type="button" className="report-modal__close" aria-label="Cerrar reporte" onClick={handleClose}>
            <CloseIcon />
          </button>
        </header>
        {alertMessage.message && (
          <div className="success-message" role={alertMessage.type === 'error' ? 'alert' : 'status'}>
            <Alert type={alertMessage.type}>{alertMessage.message}</Alert>
          </div>
        )}
        <div className="report-modal__body">
          {isWorkshopReport && (
            <section className="report-modal__section" aria-labelledby="report-period-title">
              <div className="report-modal__section-heading">
                <h4 id="report-period-title">Período</h4>
                <p>Deja las fechas vacías para incluir todo el historial.</p>
              </div>
              <div className="announcement-form__field-grid announcement-form__field-grid--two report-modal__dates">
                <label className="form-wrapper form-wrapper--full" htmlFor="workshop-report-start-date">
                  <span className="form-wrapper__label">Desde (opcional)</span>
                  <div className="text-input">
                    <input
                      id="workshop-report-start-date"
                      type="date"
                      value={startDate}
                      ref={startDateInput}
                      onChange={event => setStartDate(event.target.value)}
                      disabled={loading}
                    />
                  </div>
                </label>
                <label className="form-wrapper form-wrapper--full" htmlFor="workshop-report-end-date">
                  <span className="form-wrapper__label">Hasta (opcional)</span>
                  <div className="text-input">
                    <input
                      id="workshop-report-end-date"
                      type="date"
                      value={endDate}
                      ref={endDateInput}
                      onChange={event => setEndDate(event.target.value)}
                      disabled={loading}
                    />
                  </div>
                </label>
              </div>
              <p className="report-modal__note">Se usa la primera visualización registrada.</p>
            </section>
          )}
          <section className="report-modal__section" aria-labelledby="report-recipients-title">
            <div className="report-modal__section-heading">
              <h4 id="report-recipients-title">Destinatarios</h4>
              <p>Agrega uno o más correos para recibir el reporte.</p>
            </div>
            <EmailInput
              emails={emails}
              setEmails={setEmails}
              value={pendingEmail}
              onChange={value => { setPendingEmail(value); setEmailError(''); }}
              error={emailError}
              onAdd={addEmail}
              disabled={loading}
            />
          </section>
        </div>
        <div className="wizard__action report-modal__footer">
          <Button secondary onClick={handleClose}>Volver</Button>
          <fieldset disabled={loading} className="report-modal__submit" aria-busy={loading}>
            <Button widthFull onClick={requestReport} disabled={loading} loading={loading}>
              Generar reporte
            </Button>
          </fieldset>
        </div>
      </section>
    </Modal>
  );
};

export default ReportModal;
